// Record presentation helpers — one place to decide what badge, title,
// and column layout to use for each record type. Timeline components
// read these instead of reinventing the rules.

import type { CaseRecord, FileKind } from "../types";
import { formatDate, formatTime } from "./formatDate";

export type RecordCategory =
  | "email"
  | "video"
  | "audio"
  | "screenshot"
  | "file"
  | "phone_call"
  | "letter"
  | "sms"
  | "in_person"
  | "other";

export interface RecordDisplay {
  category: RecordCategory;
  // Short label used in Badge components — never wraps.
  typeLabel: string;
  // Emoji shown inside the type badge.
  typeEmoji: string;
  // Title string for the record row / card. Prefers the deterministic
  // content-derived label (content_label) when present — a short accurate
  // descriptor built from the record's facts — so a list is scannable even
  // when the forwarded subject is useless ("Fwd: Fwd: your claim"). Falls back
  // to filename/subject/first-OCR-line when there is no label.
  title: string;
  // The ORIGINAL subject-derived title, shown as a secondary line/tooltip
  // WHEN a content_label replaced it (so a claim number / sender in the
  // subject is never lost). Undefined when title is already the subject.
  subtitle?: string;
  // Whether from/to/via columns are meaningful for this record.
  showPeopleColumns: boolean;
}

function mimeToKind(mime: string | null): FileKind {
  if (!mime) return "other";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  if (mime === "application/pdf") return "pdf";
  if (mime.includes("wordprocessingml")) return "docx";
  if (mime.includes("spreadsheetml")) return "xlsx";
  return "other";
}

function firstLine(text: string | null | undefined): string {
  if (!text) return "";
  // Skip leading CaseThread framing markers like "[EMAIL BODY]" or
  // "[ATTACHMENT: foo.pdf]" so the displayed title shows real content.
  const lines = text.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (/^\[.*\]$/.test(line)) continue; // bare marker
    return line;
  }
  return "";
}

function truncate(s: string, n: number): string {
  const clean = s.replace(/\s+/g, " ").trim();
  return clean.length <= n ? clean : clean.slice(0, n - 1) + "…";
}

// macOS's screenshot-to-clipboard lands in paste handlers with names
// like "tempImageE18KsS.png" — never useful, never shown to humans.
// Catch the generic "image.png" too.
function isTempFilename(fname: string | null | undefined): boolean {
  if (!fname) return false;
  if (/^tempImage[\w-]*\.(png|jpg|jpeg|gif|webp)$/i.test(fname)) return true;
  if (/^image\.(png|jpg|jpeg|gif|webp)$/i.test(fname)) return true;
  if (/^Pasted image /i.test(fname)) return true; // Windows Snipping Tool
  if (/^Screenshot \d{4}-\d{2}-\d{2}/i.test(fname)) return false; // real name
  if (/^Screen Shot \d{4}-\d{2}-\d{2}/i.test(fname)) return false; // real name
  return false;
}

// Used when we can't find a meaningful OCR line and the filename is a
// paste-temp — "Screenshot — Mar 9, 2026 · 10:17". All formatting
// goes through the shared utility so the title matches the date
// printed on the record row in the list (both anchored to UTC).
function screenshotDateTitle(iso: string | null | undefined): string {
  const source = iso ?? new Date().toISOString();
  const date = formatDate(source, "short");
  if (!date) return "Screenshot";
  const time = formatTime(source);
  return time ? `Screenshot — ${date} · ${time}` : `Screenshot — ${date}`;
}

export function describeRecord(r: CaseRecord): RecordDisplay {
  // Work out the "real" category even when the source_type is generic
  // ("other") but the attached file mime reveals the intent. Screenshots
  // from the paste-canvas land as source_type='other' with an image
  // attachment, so we peek.
  const attachedMime =
    r.file_mime_type ??
    (r.attachments && r.attachments[0] ? r.attachments[0].mime_type : null);
  const attachedKind = mimeToKind(attachedMime);

  let category: RecordCategory;
  if (r.source_type === "email") category = "email";
  else if (r.source_type === "video" || attachedKind === "video")
    category = "video";
  else if (r.source_type === "phone_call" || attachedKind === "audio")
    category = "audio";
  else if (attachedKind === "image") category = "screenshot";
  else if (
    attachedKind === "pdf" ||
    attachedKind === "docx" ||
    attachedKind === "xlsx"
  )
    category = "file";
  else if (r.source_type === "letter") category = "letter";
  else if (r.source_type === "sms") category = "sms";
  else if (r.source_type === "in_person") category = "in_person";
  else category = "other";

  // Pick the title. User-typed subject always wins when present —
  // filenames, OCR, and date fallbacks only fire when the record
  // has no subject. Filename still renders in the expanded
  // attachment section, so it's never lost; it's just not allowed
  // to overwrite the user's title in the list view.
  const subject = r.subject?.trim() || "";
  let title = "";
  if (category === "email") {
    title = subject || firstLine(r.body_text) || "(no subject)";
  } else if (
    category === "video" ||
    category === "audio" ||
    category === "file"
  ) {
    const fname =
      r.file_name ??
      (r.attachments && r.attachments[0]
        ? r.attachments[0].filename
        : null);
    title = subject || fname || firstLine(r.body_text) || `(${category})`;
  } else if (category === "screenshot") {
    const ocrLine = firstLine(r.ocr_text) || firstLine(r.body_text);
    const fname =
      r.file_name ??
      (r.attachments && r.attachments[0]
        ? r.attachments[0].filename
        : null);
    if (subject) {
      title = subject;
    } else if (ocrLine) {
      title = ocrLine;
    } else if (fname && !isTempFilename(fname)) {
      title = fname;
    } else {
      title = screenshotDateTitle(r.record_date ?? r.logged_at);
    }
  } else {
    title = subject || firstLine(r.body_text) || "(untitled)";
  }
  title = truncate(title, 140);

  // Prefer the deterministic content label as the primary title. Keep the
  // original subject-derived title as a subtitle so nothing is lost — but only
  // when it adds information (differs from the label and isn't a bare
  // placeholder like "(no subject)").
  let subtitle: string | undefined;
  const label = r.content_label?.trim();
  if (label) {
    const original = title;
    subtitle =
      original && original !== label && !/^\(.*\)$/.test(original)
        ? original
        : undefined;
    title = truncate(label, 140);
  }

  const typeLabel = labelForCategory(category, attachedKind);
  // Manual-record override: the pen icon signals user-authored
  // provenance (Write record modal), separate from the underlying
  // source_type. Keep the category + label semantics — source_type
  // still classifies the content; creation_source just flags that
  // the user typed it rather than uploading/forwarding.
  const typeEmoji =
    r.creation_source === "manual"
      ? "✏️"
      : emojiForCategory(category, attachedKind);

  return {
    category,
    typeLabel,
    typeEmoji,
    title,
    subtitle,
    // Show from/to columns for email/letter as before, plus any
    // manual record that actually populated those fields — a written
    // phone-call note where the user logged both parties benefits
    // from the same header layout.
    showPeopleColumns:
      category === "email" ||
      category === "letter" ||
      (r.creation_source === "manual" && (!!r.from_addr || !!r.to_addr)),
  };
}

// Human-readable labels. An audio record is almost always a phone
// call in this product (the Record-call flow + phone_call source
// type both land here), so the column reads "Phone call" rather
// than the more literal "Audio". Screenshots read as "Image" —
// users took a photo or screenshot, the file-vs-source distinction
// isn't interesting to them. "File"-category records (PDFs, Word,
// Excel) unify under "Document"; the specific format lives in the
// attachment card below the fold.
function labelForCategory(
  c: RecordCategory,
  kind: FileKind,
): string {
  if (c === "email") return "Email";
  if (c === "video") return "Video";
  if (c === "audio") return "Phone call";
  if (c === "screenshot") return "Image";
  if (c === "file") {
    if (kind === "pdf") return "PDF";
    if (kind === "docx") return "Word";
    if (kind === "xlsx") return "Excel";
    return "Document";
  }
  if (c === "letter") return "Letter";
  if (c === "sms") return "SMS";
  if (c === "in_person") return "In person";
  return "Note";
}

function emojiForCategory(c: RecordCategory, kind: FileKind): string {
  if (c === "email") return "✉️";
  if (c === "video") return "🎥";
  if (c === "audio") return "☎️";
  if (c === "screenshot") return "📷";
  if (c === "file") {
    if (kind === "pdf") return "📄";
    if (kind === "docx") return "📝";
    if (kind === "xlsx") return "📊";
    return "📄";
  }
  if (c === "letter") return "✉️";
  if (c === "sms") return "💬";
  if (c === "in_person") return "🗣";
  return "✏️";
}

// Short direction label used in the type badge — "In" / "Out".
export function directionLabel(direction: CaseRecord["direction"]): string {
  return direction === "inbound" ? "In" : "Out";
}
